import React, { useEffect, useState } from 'react';
import 'react-toastify/dist/ReactToastify.css';
import * as S from './PermissionsStyles';
import Add from '../../../assets/buttons/add.svg';
import CollapseList from '../../../components/profiles-collapse';
import { usePermissions } from '../../../hooks/usePermissions';
import { IProfileGroup } from '../../../interfaces';
import {
  Loading,
  LoadingCircle,
  ModalResponsive,
  Pagination,
} from '../../../components';
import { ToastContainer, Slide, toast } from 'react-toastify';
import { RegitrationFormPermissions } from './RegistrationPermissionForm/RegistrationPermissionForm';
import { usePagination } from '../../../hooks/usePagination';

export const Permissions: React.FC = () => {
  const [
    {
      profileGroups,
      profilePagination,
      permissionsProfile,
      isLoading,
      modalLoading,
      error,
    },
    fetchProfilesGroups,
    fetchPermissionProfiles,
    insertOrEditPermissions,
    toggleActivatePermission,
    removeProfile,
  ] = usePermissions();

  const [openManagementModal, setOpenManagementModal] = useState(false);
  const [openActionModal, setOpenActionModal] = useState(false);
  const [openConfirmationModal, setOpenConfirmationModal] = useState(false);

  const [modalAction, setModalAction] = useState('');
  const [modalData, setModalData] = useState<IProfileGroup | any>({});
  const [actionType, setActionType] = useState<'toggle' | 'remove'>('toggle');

  const {
    currentPage,
    pageSize,
    handlePageChange,
    resetPagination,
    setCurrentPage,
  } = usePagination({
    totalItems: profilePagination.totalItems || 0,
    onPageChange: (currentPage, pageSize) => {
      fetchProfilesGroups({ pageSize, currentPage });
    },
  });

  const handleOpenModal = (action: string, data?: IProfileGroup | any) => {
    setModalData(data);
    setModalAction(action);
    setOpenManagementModal(true);
  };

  const handleOpenActionModal = (
    dataChild: IProfileGroup,
    type: 'toggle' | 'remove',
  ) => {
    setModalData(dataChild);
    setActionType(type);
    if (dataChild.qtdUsuarios > 0) {
      setOpenConfirmationModal(true);
    } else {
      setOpenActionModal(true);
    }
  };

  const handleSave = (dataChild: IProfileGroup | any) => {
    setCurrentPage(1);
    insertOrEditPermissions(dataChild, modalAction !== 'criar' ? true : false);
    setOpenManagementModal(false);
  };

  const handleCancel = () => {
    setOpenManagementModal(false);
  };

  useEffect(() => {
    fetchProfilesGroups({ pageSize, currentPage: 1 });
    fetchPermissionProfiles();
  }, []);

  const [load, setLoading] = useState(false);

  const handleConfirmAction = async () => {
    try {
      setLoading(true);
      if (actionType === 'toggle') {
        await toggleActivatePermission(modalData.id, !modalData.ativo);
        toast.success(
          modalData.ativo
            ? 'Perfil desativado com sucesso!'
            : 'Perfil reativado com sucesso!',
        );
      } else if (actionType === 'remove') {
        await removeProfile(modalData.id);
        toast.success('Perfil removido com sucesso!');
      }
      setOpenActionModal(false);
      setOpenActionModal(false);
    } catch (error) {
      toast.error(
        `Erro ao ${
          actionType === 'toggle' ? 'alterar status' : 'remover'
        } o perfil.`,
      );
    } finally {
      setLoading(false);
      setCurrentPage(1);
      fetchProfilesGroups({ pageSize, currentPage: 1 });
    }
  };

  const getActionModalTitle = () => {
    if (actionType === 'toggle') {
      return `Tem certeza que deseja ${
        modalData.ativo ? 'desativar' : 'reativar'
      } esse perfil?`;
    } else {
      return 'Tem certeza que deseja excluir este perfil definitivamente?';
    }
  };

  const getActionModalText = () => {
    if (actionType === 'toggle') {
      return `Ao ${modalData.ativo ? 'desativar' : 'reativar'} este perfil, ${
        modalData.ativo
          ? 'não ficará mais disponível para nenhum usuário'
          : 'ficará disponível para todos os usuários'
      }.`;
    } else {
      return 'Esta ação é irreversível e removerá o perfil permanentemente, incluindo todas as associações com usuários.';
    }
  };

  const getConfirmButtonText = () => {
    if (actionType === 'toggle') {
      return modalData.ativo ? 'Desativar' : 'Reativar';
    } else {
      return 'Excluir definitivamente';
    }
  };

  const getConfirmationText = () => {
    const actionVerb = actionType === 'toggle' ? 'desativar' : 'remover';
    return `Para poder ${actionVerb} este perfil, você deve remover os usuários que estão relacionados ao mesmo.`;
  };

  return (
    <S.Grid.Container>
      <S.Grid.Content>
        <S.RowContent>
          <S.Text>
            Confira os dados, altere ou edite perfis de usuários na lista
            abaixo.
          </S.Text>
          <S.AddButton onClick={() => handleOpenModal('criar', {})}>
            <img src={Add} width={12} />
            Novo perfil de acesso
          </S.AddButton>
        </S.RowContent>
        <div style={{ textAlign: 'center' }}>{isLoading && <Loading />}</div>
        {!isLoading &&
          profileGroups.map((group: IProfileGroup, index: number) => (
            <CollapseList
              key={index}
              id={group.id}
              nome={group.nome}
              qtdUsuarios={group.qtdUsuarios}
              ativo={group.ativo}
              perfilRevenda={group.perfilRevenda}
              permissionsProfile={permissionsProfile}
              log={{
                dataCriacaoLog: group.dataCriacaoLog,
                usuarioAlteracaoPerfil: group.usuarioAlteracaoPerfil,
              }}
              permissoes={group.permissoes}
              onEditButtonClick={(data: IProfileGroup | any) =>
                handleOpenModal('editar', data)
              }
              onDeactivateButtonClick={(data: IProfileGroup | any) =>
                handleOpenActionModal(data, 'toggle')
              }
              onRemoveButtonClick={(data: IProfileGroup | any) =>
                handleOpenActionModal(data, 'remove')
              }
            />
          ))}
        <Pagination
          currentPage={currentPage}
          paginationSize={pageSize}
          onPageChange={handlePageChange}
          totalItems={profilePagination.totalItems}
        />
      </S.Grid.Content>

      {openManagementModal && (
        <ModalResponsive
          title={
            modalAction === 'criar'
              ? 'Criação do perfil de acesso'
              : 'Edição do perfil de acesso'
          }
          isOpenModal={openManagementModal}
          handleOpenModal={() => setOpenManagementModal(false)}
        >
          <RegitrationFormPermissions
            permissions={permissionsProfile}
            profileGroup={modalAction === 'criar' ? false : modalData}
            onSave={handleSave}
            onCancel={handleCancel}
            load={modalLoading || false}
          />
        </ModalResponsive>
      )}

      {openActionModal && (
        <ModalResponsive
          title={getActionModalTitle()}
          isOpenModal={openActionModal}
          handleOpenModal={() => setOpenActionModal(false)}
        >
          <S.Text className="small">{getActionModalText()}</S.Text>
          <S.Buttons.ButtonsContainer className="right">
            <S.Buttons.IconButton onClick={() => setOpenActionModal(false)}>
              Cancelar
            </S.Buttons.IconButton>
            <S.Buttons.IconButton
              className={
                actionType === 'remove' ? 'danger bold' : 'success bold'
              }
              onClick={handleConfirmAction}
              disabled={load}
            >
              {load ? <LoadingCircle /> : getConfirmButtonText()}
            </S.Buttons.IconButton>
          </S.Buttons.ButtonsContainer>
        </ModalResponsive>
      )}

      {openConfirmationModal && (
        <ModalResponsive
          title="Ops! Perfil com usuários relacionados."
          isOpenModal={openConfirmationModal}
          handleOpenModal={() => setOpenConfirmationModal(false)}
        >
          <S.Text className="small">{getConfirmationText()}</S.Text>
          <S.Buttons.ButtonsContainer className="right">
            <S.Buttons.IconButton
              className="update"
              onClick={() => setOpenConfirmationModal(false)}
            >
              Ok
            </S.Buttons.IconButton>
          </S.Buttons.ButtonsContainer>
        </ModalResponsive>
      )}

      <ToastContainer
        closeButton={false}
        transition={Slide}
        position="bottom-center"
        autoClose={2000}
        hideProgressBar
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="colored"
      />
    </S.Grid.Container>
  );
};
